// Core dice and stat rules, modelled on Might and Magic III / World of Xeen.

// Attribute thresholds and the bonus granted once a stat reaches each one.
const STAT_THRESHOLDS = [3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 25, 30, 35, 40, 50, 75, 100, 125, 150, 175, 200, 225, 250];
const STAT_BONUSES = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

export const BLOCK_AC_BONUS = 4;

export function statBonus(value) {
    let idx = 0;
    while (idx < STAT_THRESHOLDS.length && STAT_THRESHOLDS[idx] <= value) {
        idx++;
    }
    return STAT_BONUSES[idx];
}

export function rollDie(sides, rng = Math.random) {
    return 1 + Math.floor(rng() * sides);
}

export function rollDice([count, sides], rng = Math.random) {
    let total = 0;
    for (let i = 0; i < count; i++) {
        total += rollDie(sides, rng);
    }
    return total;
}

export function rollRange(min, max, rng = Math.random) {
    return min + Math.floor(rng() * (max - min + 1));
}

// d20 that keeps rolling and adding while it comes up 20 (as in MM3's to-hit
// roll). Capped, so a fixed or broken generator cannot spin here forever.
const MAX_EXPLOSIONS = 6;

export function rollExplodingD20(rng = Math.random) {
    const natural = rollDie(20, rng);
    let total = natural;
    let last = natural;
    for (let rolls = 0; last === 20 && rolls < MAX_EXPLOSIONS; rolls++) {
        last = rollDie(20, rng);
        total += last;
    }
    return { natural, total };
}

// A strike lands when the roll plus bonuses meets the defender's AC + 10
export function strikeHits(hitBonus, defenderAC, rng = Math.random) {
    const { natural, total } = rollExplodingD20(rng);
    if (natural === 1) {
        return false;
    }
    return total + hitBonus >= defenderAC + 10;
}

export function isAble(character) {
    return character.condition === 'good';
}

export function characterAC(character, blocking = false) {
    return character.armor + statBonus(character.stats.speed) + (blocking ? BLOCK_AC_BONUS : 0);
}

export function meleeHitBonus(character) {
    return statBonus(character.stats.accuracy) + character.level + (character.weapon.toHit || 0);
}

export function missileHitBonus(character) {
    return statBonus(character.stats.accuracy) + character.level + (character.missile.toHit || 0);
}

export function meleeDamage(character, rng = Math.random) {
    return Math.max(1, rollDice(character.weapon.dice, rng) + statBonus(character.stats.might) + (character.weapon.damageBonus || 0));
}

export function missileDamage(character, rng = Math.random) {
    return Math.max(1, rollDice(character.missile.dice, rng) + (character.missile.damageBonus || 0));
}

// HP at or below zero knocks a character out; falling past -Endurance kills them
export function applyCharacterDamage(character, damage) {
    character.hp -= damage;
    character.hitCount = (character.hitCount || 0) + 1;
    if (character.hp <= -character.stats.endurance) {
        character.condition = 'dead';
    } else if (character.hp <= 0) {
        character.condition = 'unconscious';
    }
    return character.condition;
}

export function healCharacter(character, amount) {
    if (character.condition === 'dead') {
        return 0;
    }
    const before = character.hp;
    character.hp = Math.min(character.maxHp, character.hp + amount);
    if (character.hp > 0) {
        character.condition = 'good';
    }
    return character.hp - before;
}
